import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function PropertyDetails() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('8.5');
  const [loanTenure, setLoanTenure] = useState('20');
  const [monthlyEMI, setMonthlyEMI] = useState(null);

  const formatIndianPrice = (amount) => {
    if (!amount && amount !== 0) return '';
    const num = Number(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
  };

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const docRef = doc(db, "properties", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProperty(data);
          
          const numericPrice = parseFloat(data.price);
          setLoanAmount(isNaN(numericPrice) ? '5000000' : numericPrice);
        } else {
          console.log("No such property found!");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching property details: ", error);
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const calculateEMI = (e) => {
    e.preventDefault();
    const p = parseFloat(loanAmount);
    const r = parseFloat(interestRate) / 12 / 100;
    const n = parseFloat(loanTenure) * 12;

    if (p > 0 && r > 0 && n > 0) {
      const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setMonthlyEMI(Math.round(emi));
    }
  };

  if (loading) {
    return <div className="text-center py-24 text-slate-500 font-medium">Loading property details...</div>;
  }

  if (!property) {
    return (
      <div className="text-center py-24">
        <h2 className="text-2xl font-bold text-slate-800">Property not found</h2>
        <Link to="/" className="text-indigo-600 hover:underline mt-4 inline-block">Back to Home</Link>
      </div>
    );
  }

  const whatsappMessage = encodeURIComponent(`Hi, I am interested in your property titled "${property.title}" located at ${property.location} listed on PrimiumFlats.`);
  const ownerPhone = property.phone || "+919876543210";

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
        
        {/* Property Image Header */}
        <div className="relative h-80 md:h-[420px] w-full">
          <img 
            src={property.image || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2"} 
            alt={property.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <span className="bg-indigo-600 text-xs font-semibold px-3 py-1 rounded-lg uppercase tracking-wider">
              {property.type || "Flat"}
            </span>
            <h1 className="text-2xl md:text-4xl font-extrabold mt-2">{property.title}</h1>
            <p className="text-slate-200 text-sm md:text-base mt-1">{property.location}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-10">
          
          {/* Left Details */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Property Overview</h2>
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-slate-400 block">Asking Price</span>
                  <span className="text-2xl font-extrabold text-indigo-600">₹ {formatIndianPrice(property.price)}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Builder / Owner</span>
                  <span className="text-lg font-bold text-slate-800">{property.builder || "Verified Builder"}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Super Built-up Area</span>
                  <span className="text-sm font-semibold text-slate-800">{property.area || "N/A"}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Furnishing Status</span>
                  <span className="text-sm font-semibold text-slate-800">{property.furnishing || "Semi-Furnished"}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Bathrooms</span>
                  <span className="text-sm font-semibold text-slate-800">{property.bathrooms || "2"}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Posted By</span>
                  <span className="text-sm font-semibold text-slate-800">{property.ownerEmail || "Verified Owner"}</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Description</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Experience world-class luxury living in this magnificent property developed by {property.builder || "top builders"} in {property.location}. Designed with modern architecture, premium fixtures, and ample natural light. Perfect for modern families looking for comfort and sophistication.
              </p>
            </div>

            {/* EMI Calculator Section */}
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Home Loan EMI Calculator</h2>
              <form onSubmit={calculateEMI} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Loan Amount (₹) {loanAmount && <span className="text-indigo-600 font-normal">({formatIndianPrice(loanAmount)})</span>}
                  </label>
                  <input 
                    type="number" 
                    value={loanAmount} 
                    onChange={(e) => setLoanAmount(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Interest Rate (% p.a.)</label>
                    <input 
                      type="number" 
                      step="0.1" 
                      value={interestRate} 
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Tenure (Years)</label>
                    <input 
                      type="number" 
                      value={loanTenure} 
                      onChange={(e) => setLoanTenure(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2.5 rounded-xl text-sm font-medium transition">
                  Calculate EMI
                </button>
              </form>

              {monthlyEMI !== null && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-center">
                  <span className="text-xs text-indigo-600 font-semibold block">Estimated Monthly Payment</span>
                  <span className="text-2xl font-extrabold text-indigo-900">₹ {formatIndianPrice(monthlyEMI)} / month</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Action Card */}
          <div>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 sticky top-24 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Interested in this property?</h3>
              <p className="text-xs text-slate-500">Connect instantly with the builder/owner via call or WhatsApp.</p>
              
              <a 
                href={`tel:${ownerPhone}`} 
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-medium text-sm transition shadow-md"
              >
                📞 Call Owner Now
              </a>

              <a 
                href={`https://wa.me/${ownerPhone.replace('+', '')}?text=${whatsappMessage}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium text-sm transition shadow-md"
              >
                💬 Chat on WhatsApp
              </a>

              <Link 
                to="/" 
                className="block text-center text-xs text-slate-500 hover:text-indigo-600 pt-2 transition"
              >
                &larr; Back to all properties
              </Link>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}